"use client";

import {
  WorkplaceRequestTypesParams,
  WorkplaceRequestTypesResponse
} from "@gettactic/api";
import { useAuthenticated } from "@gettactic/hooks";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/api";
import { canScheduleOthers } from "@/lib/permissions";

/**
 * The `/v1/tickets/types` endpoint takes neither `limit` nor `cursor` and always
 * answers with every type in one page, so this is a plain query — there is no
 * second page to fetch.
 */
export function useWorkplaceRequestTypes(
  filters: WorkplaceRequestTypesParams = {},
  options: {
    enabled?: boolean;
  } = {}
) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isAdmin = canScheduleOthers(authenticatedUser);

  const { data, error, refetch, isFetching, status } =
    useQuery<WorkplaceRequestTypesResponse>({
      queryKey: ["workplaceRequestTypes", filters, isAdmin],
      queryFn: async () => {
        const res =
          await api.client.workplaceRequestTypes.getWorkplaceRequestTypes({
            ...filters,
            isAdmin
          });
        return res.result as WorkplaceRequestTypesResponse;
      },
      enabled: true,
      ...options
    });

  const allElements = data?.elements ?? [];

  return {
    data,
    error,
    refetch,
    isFetching,
    status,
    allElements,
    totalElements: allElements.length
  };
}
